import { BaseElement } from '../../base-element';
import { trialsApi } from '../../../api/trials.api';
import { entriesApi } from '../../../api/entries.api';
import { termsApi } from '../../../api/terms.api';
import { apiGet } from '../../../api/client';
import type { components } from '../../../api/generated/api.models';
import type {
  EntryDetailDto,
  EntryUpdateRequestDto,
  TrialEventSummaryDto,
} from '../../../api/types';
import {
  formActions,
  formState,
  type ContactUpdate,
  type ContactFields,
  type DogFields,
  type EmergencyContactFields,
  type FeesFields,
  type FormStateShape,
  type ValidationError,
} from '../../../state/form.state';
import { toastActions } from '../../../state/toast.state';
import { navigate } from '../../../router';
import {
  registrationActions,
  registrationComputed,
  registrationState,
} from './registration-state';
import { buildErrorMessage } from './sections/validation-summary';
import { editRunSelections, runErrorControlPath, type RunSelectionDraft } from '../../../services/run-selections';
import type { ActualRunsEditor } from './sections/actual-runs-editor';
import type { FeesSection } from './sections/fees-section';
import { hasRegistrationActualTrialConfiguration } from '../../../api/actual-trials';
import './sections/dog-section';
import './sections/contact-section';
import './sections/emergency-section';
import './sections/fees-section';
import './sections/terms-section';
import './sections/actual-runs-editor';
import './sections/validation-summary';
import './sections/premium-section';
import '../../ui/dt-card';
import '../../ui/dt-button';
import '../../ui/dt-spinner';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
const zipRegex = /^\d{5}$/;

type CreateDraftResponse = Awaited<ReturnType<typeof entriesApi.createDraft>>;

const draftCreateRequests = new Map<string, Promise<CreateDraftResponse>>();

function getCreateDraftRequest(trialEventId: string): Promise<CreateDraftResponse> {
  const existingRequest = draftCreateRequests.get(trialEventId);
  if (existingRequest) {
    return existingRequest;
  }

  const request = entriesApi.createDraft(trialEventId);

  let trackedRequest!: Promise<CreateDraftResponse>;
  trackedRequest = request.finally(() => {
    if (draftCreateRequests.get(trialEventId) === trackedRequest) {
      draftCreateRequests.delete(trialEventId);
    }
  });

  draftCreateRequests.set(trialEventId, trackedRequest);
  return trackedRequest;
}

export class RegistrationPage extends BaseElement {
  private trialEventId = '';
  private autoSaveTimer: number | null = null;
  private entryStatus = 'Draft';
  private persistenceQueue: Promise<void> = Promise.resolve();
  private registrationLoadPromise: Promise<void> | null = null;
  private registrationLoadTrialEventId: string | null = null;
  private registrationLoadRequestId = 0;
  private quoteRequestId = 0;
  private savedFields = '';
  private savedJuniorDob = '';

  static get observedAttributes(): string[] {
    return ['trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
      if (this.isConnected) {
        void this.loadRegistration();
      }
    }
  }

  connectedCallback(): void {
    this.watch(formState, () => this.updateRender());
    this.watch(registrationState.loading, () => this.updateRender());
    this.watch(registrationState.error, () => this.updateRender());
    this.watch(registrationState.trial, () => this.updateRender());
    this.watch(registrationState.formMetadata, () => this.updateRender());
    this.watch(registrationState.terms, () => this.updateRender());
    this.watch(registrationState.validationSummaryVisible, () => this.updateRender());
    this.watch(registrationState.submitErrorMessage, () => this.updateRender());
    this.watch(registrationState.submitSupportId, () => this.updateRender());
    this.updateRender();

    if (this.trialEventId) {
      void this.loadRegistration();
    }
  }

  override disconnectedCallback(): void {
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }

    super.disconnectedCallback();
  }

  private loadRegistration(): Promise<void> {
    if (!this.trialEventId) {
      registrationActions.setError('Missing trial identifier.');
      registrationActions.setLoading(false);
      return Promise.resolve();
    }

    if (
      this.registrationLoadPromise &&
      this.registrationLoadTrialEventId === this.trialEventId
    ) {
      return this.registrationLoadPromise;
    }

    const requestId = ++this.registrationLoadRequestId;
    const trialEventId = this.trialEventId;
    const loadPromise = Promise.resolve().then(() => this.performLoadRegistration(trialEventId, requestId));

    this.registrationLoadTrialEventId = trialEventId;
    const trackedPromise = loadPromise.finally(() => {
      if (this.registrationLoadPromise === trackedPromise) {
        this.registrationLoadPromise = null;
        this.registrationLoadTrialEventId = null;
      }
    });
    this.registrationLoadPromise = trackedPromise;

    return this.registrationLoadPromise;
  }

  private isActiveRegistrationLoad(requestId: number, trialEventId: string): boolean {
    return this.isConnected && this.registrationLoadRequestId === requestId && this.trialEventId === trialEventId;
  }

  private async performLoadRegistration(trialEventId: string, requestId: number): Promise<void> {
    if (!trialEventId) {
      return;
    }

    registrationActions.reset();
    formActions.initializeForTrial(trialEventId);
    registrationActions.setLoading(true);
    registrationActions.setError(null);

    try {
      const [trialResponse, metadataResponse, termsResponse, draftResponse, configResponse] =
        await Promise.all([
          trialsApi.getById(trialEventId),
          trialsApi.getRegistrationMetadata(trialEventId),
          termsApi.getCurrent(),
          getCreateDraftRequest(trialEventId),
          apiGet<components['schemas']['RegistrationPageDto']>(
            `/trial-events/${trialEventId}/registration`
          ),
        ]);

      if (!this.isActiveRegistrationLoad(requestId, trialEventId)) {
        return;
      }

      if (trialResponse.error || !trialResponse.data) {
        registrationActions.setError('Unable to load trial details. Please try again.');
        return;
      }

      if (metadataResponse.error || !metadataResponse.data) {
        registrationActions.setError('Unable to load registration metadata. Please try again.');
        return;
      }

      if (termsResponse.error || !termsResponse.data) {
        registrationActions.setError('Unable to load registration metadata. Please try again.');
        return;
      }

      if (draftResponse.error) {
        registrationActions.setError('Unable to create a draft entry. Please try again.');
        return;
      }

      if (configResponse.error || !configResponse.data?.trialConfig) {
        registrationActions.setError('Unable to load trial fees. Please try again.');
        return;
      }

      if (!hasRegistrationActualTrialConfiguration(configResponse.data.trialConfig) ||
          !hasRegistrationActualTrialConfiguration(metadataResponse.data.trialConfig) ||
          configResponse.data.trialConfig.configurationVersion !== metadataResponse.data.trialConfig.configurationVersion) {
        registrationActions.setError('Unable to load current actual-trial configuration. Please reload the application.');
        return;
      }

      registrationActions.setTrial(trialResponse.data);
      registrationActions.setTrialConfig(configResponse.data.trialConfig);
      registrationActions.setFormMetadata(metadataResponse.data.formMetadata);
      registrationActions.setTerms(termsResponse.data);

      if (draftResponse.data?.entryId) {
        const draftEntryResponse = await entriesApi.getById(
          trialEventId,
          draftResponse.data.entryId
        );

        if (!this.isActiveRegistrationLoad(requestId, trialEventId)) {
          return;
        }

        if (draftEntryResponse.error || !draftEntryResponse.data) {
          registrationActions.setError('Unable to initialize the draft entry. Please try again.');
          return;
        }

        const entryEtag = draftEntryResponse.etag ?? draftResponse.etag ?? null;

        if (!entryEtag) {
          registrationActions.setError('Unable to initialize the draft entry. Please try again.');
          return;
        }

        formActions.loadEntry(draftResponse.data.entryId, {
          ...this.mapEntryToFormState(draftEntryResponse.data),
          entryEtag,
        });
        this.entryStatus = draftEntryResponse.data.status;
        this.savedJuniorDob = draftEntryResponse.data.contact.junior?.dob ?? '';
        this.savedFields = JSON.stringify(this.buildEntryUpdateRequest());
        formActions.markClean();
        if (this.entryStatus === 'Draft') await this.refreshValidation();
      }
    } finally {
      if (this.isActiveRegistrationLoad(requestId, trialEventId)) {
        registrationActions.setLoading(false);
      }
    }
  }

  private parseDateOnly(value: string): Date {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }

    return new Date(value);
  }

  private formatDateRange(trial: TrialEventSummaryDto): string {
    if (!trial.startDate || !trial.endDate) {
      return 'Date TBD';
    }

    const start = this.parseDateOnly(trial.startDate);
    const end = this.parseDateOnly(trial.endDate);
    const sameDay = trial.startDate === trial.endDate;
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

    const fullStartFormat = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (sameDay) {
      return fullStartFormat;
    }

    const startFormat = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

    if (sameMonth) {
      const endFormat = `${end.getDate()}, ${end.getFullYear()}`;

      return `${startFormat} - ${endFormat}`;
    }

    const endFormat = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startFormat} - ${endFormat}`;
  }

  private isPastDate(value: string): boolean {
    if (!value) return false;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed < today;
  }

  private shouldInclude(path: string, includeAll: boolean, touched: Set<string>): boolean {
    return includeAll || touched.has(path);
  }

  private addError(
    errors: ValidationError[],
    path: string,
    code: string,
    includeAll: boolean,
    touched: Set<string>
  ): void {
    if (!this.shouldInclude(path, includeAll, touched)) return;
    errors.push({
      field: path,
      code,
      message: buildErrorMessage(path, code),
    });
  }

  private validateForm(options: {
    includeAll?: boolean;
    includeTerms?: boolean;
    includeSelections?: boolean;
  } = {}): ValidationError[] {
    const state = formState.get();
    const includeAll = options.includeAll ?? false;
    const touched = registrationState.touchedFields.get();
    const errors: ValidationError[] = [];

    const requiredDog: Array<[string, string]> = [
      ['dog.ascaRegistrationNumber', state.dog.ascaRegistrationNumber],
      ['dog.breed', state.dog.breed],
      ['dog.registeredName', state.dog.registeredName],
      ['dog.callName', state.dog.callName],
      ['dog.dob', state.dog.dob],
      ['dog.sex', state.dog.sex],
    ];

    requiredDog.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.dog.dob && !this.isPastDate(state.dog.dob)) {
      this.addError(errors, 'dog.dob', 'pastDate', includeAll, touched);
    }

    const requiredContact: Array<[string, string]> = [
      ['contact.owners', state.contact.owners],
      ['contact.ownerAddress.street', state.contact.ownerAddress.street],
      ['contact.ownerAddress.city', state.contact.ownerAddress.city],
      ['contact.ownerAddress.state', state.contact.ownerAddress.state],
      ['contact.ownerAddress.zip', state.contact.ownerAddress.zip],
      ['contact.ownerEmail', state.contact.ownerEmail],
      ['contact.phone', state.contact.phone],
      ['contact.handlerEmail', state.contact.handlerEmail],
    ];

    requiredContact.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.contact.ownerEmail && !emailRegex.test(state.contact.ownerEmail)) {
      this.addError(errors, 'contact.ownerEmail', 'email', includeAll, touched);
    }

    if (state.contact.handlerEmail && !emailRegex.test(state.contact.handlerEmail)) {
      this.addError(errors, 'contact.handlerEmail', 'email', includeAll, touched);
    }

    if (state.contact.phone && !phoneRegex.test(state.contact.phone)) {
      this.addError(errors, 'contact.phone', 'pattern', includeAll, touched);
    }

    if (state.contact.ownerAddress.zip && !zipRegex.test(state.contact.ownerAddress.zip)) {
      this.addError(errors, 'contact.ownerAddress.zip', 'pattern', includeAll, touched);
    }

    const requiredEmergency: Array<[string, string]> = [
      ['emergencyContact.name', state.emergencyContact.name],
      ['emergencyContact.phoneOrNumber', state.emergencyContact.phoneOrNumber],
    ];

    requiredEmergency.forEach(([path, value]) => {
      if (!value.trim()) {
        this.addError(errors, path, 'required', includeAll, touched);
      }
    });

    if (state.emergencyContact.phoneOrNumber && !phoneRegex.test(state.emergencyContact.phoneOrNumber)) {
      this.addError(errors, 'emergencyContact.phoneOrNumber', 'pattern', includeAll, touched);
    }

    if (options.includeSelections || includeAll) {
      if (!(state.runDraft?.runs.length ?? state.runSelections?.runs.length)) {
        this.addError(errors, 'selections', 'minSelections', includeAll, touched);
      }
    }

    if (options.includeTerms || includeAll) {
      if (!state.terms.accepted) {
        this.addError(errors, 'terms.accepted', 'terms', includeAll, touched);
      }
    }

    const serverErrors = state.validationErrors.filter(error => error.code === 'server');
    formActions.setValidationErrors([...errors, ...serverErrors]);
    return [...errors, ...serverErrors];
  }

  private mapEntryToFormState(entry: EntryDetailDto): Partial<FormStateShape> {
    const value = (input: string | null | undefined): string => input ?? '';

    return {
      dog: {
        ascaRegistrationNumber: value(entry.dog.ascaRegistrationNumber),
        breed: value(entry.dog.breed),
        registeredName: value(entry.dog.registeredName),
        callName: value(entry.dog.callName),
        dob: value(entry.dog.dob),
        color: value(entry.dog.color),
        sex: value(entry.dog.sex),
        sire: value(entry.dog.sire),
        dam: value(entry.dog.dam),
        breeders: value(entry.dog.breeders),
      },
      contact: {
        owners: value(entry.contact.owners),
        ownerAddress: {
          street: value(entry.contact.ownerAddress?.street),
          city: value(entry.contact.ownerAddress?.city),
          state: value(entry.contact.ownerAddress?.state),
          zip: value(entry.contact.ownerAddress?.zip),
        },
        ownerEmail: value(entry.contact.ownerEmail),
        phone: value(entry.contact.phone),
        handler: value(entry.contact.handler),
        handlerEmail: value(entry.contact.handlerEmail),
        membershipNumber: value(entry.contact.membershipNumber),
        junior: {
          dob: value(entry.contact.junior?.dob),
          memberId: value(entry.contact.junior?.memberId),
        },
      },
      emergencyContact: {
        name: value(entry.emergencyContact.name),
        phoneOrNumber: value(entry.emergencyContact.phoneOrNumber),
      },
      fees: {
        totalEntryFees: value(entry.pricingQuote?.total.toString()),
        currency: value(entry.fees.currency) || 'USD',
      },
      terms: {
        accepted: entry.terms.acceptedAtUtc != null,
        version: value(entry.terms.version),
      },
      selections: [],
      runSelections: structuredClone(entry.selections),
      runDraft: editRunSelections(entry.selections),
      entryRevision: entry.revision,
      pricingQuote: entry.pricingQuote,
    };
  }

  private buildEntryUpdateRequest(state = formState.get()): EntryUpdateRequestDto {
    const juniorDob = state.contact.junior.dob;
    const juniorMemberId = state.contact.junior.memberId;

    return {
      dog: {
        ascaRegistrationNumber: state.dog.ascaRegistrationNumber,
        breed: state.dog.breed,
        registeredName: state.dog.registeredName,
        ...(state.dog.dob ? { dob: state.dog.dob } : {}),
        color: state.dog.color,
        callName: state.dog.callName,
        sex: state.dog.sex,
        sire: state.dog.sire,
        dam: state.dog.dam,
        breeders: state.dog.breeders,
      },
      contact: {
        owners: state.contact.owners,
        ownerAddress: {
          street: state.contact.ownerAddress.street,
          city: state.contact.ownerAddress.city,
          state: state.contact.ownerAddress.state,
          zip: state.contact.ownerAddress.zip,
        },
        ownerEmail: state.contact.ownerEmail,
        phone: state.contact.phone,
        handler: state.contact.handler,
        handlerEmail: state.contact.handlerEmail,
        membershipNumber: state.contact.membershipNumber,
        junior: {
          ...(juniorDob || this.savedJuniorDob ? { dob: juniorDob } : {}),
          memberId: juniorMemberId,
        },
      },
      emergencyContact: {
        name: state.emergencyContact.name,
        phoneOrNumber: state.emergencyContact.phoneOrNumber,
      },
    };
  }

  private applyServerErrors(errors?: Record<string, string[]>): void {
    if (!errors) return;

    const serverErrors: ValidationError[] = [];
    Object.entries(errors).forEach(([path, messages]) => {
      const message = messages.join(' ');
      const field = runErrorControlPath(path);
      serverErrors.push({ field, code: 'server', message });
      registrationActions.markTouched(field);
    });

    const existing = formState.get().validationErrors.filter((error) => error.code !== 'server');
    const existingMap = new Map(existing.map((error) => [error.field, error]));
    serverErrors.forEach((error) => {
      existingMap.set(error.field, error);
    });

    formActions.setValidationErrors(Array.from(existingMap.values()));
  }

  private handleSectionChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ section: string; field: string; path: string; value: string }>).detail;
    if (!detail) return;

    if (detail.section === 'dog') {
      formActions.updateDog({ [detail.field]: detail.value } as Partial<DogFields>);
    }

    if (detail.section === 'contact') {
      if (detail.field.startsWith('ownerAddress.')) {
        const field = detail.field.replace('ownerAddress.', '') as keyof ContactFields['ownerAddress'];
        const addressUpdate: Partial<ContactFields['ownerAddress']> = {};
        addressUpdate[field] = detail.value;
        formActions.updateContact({ ownerAddress: addressUpdate });
      } else if (detail.field.startsWith('junior.')) {
        const field = detail.field.replace('junior.', '') as keyof ContactFields['junior'];
        const juniorUpdate: Partial<ContactFields['junior']> = {};
        juniorUpdate[field] = detail.value;
        formActions.updateContact({ junior: juniorUpdate });
      } else {
        formActions.updateContact({ [detail.field]: detail.value } as ContactUpdate);
      }
    }

    if (detail.section === 'emergencyContact') {
      formActions.updateEmergencyContact({ [detail.field]: detail.value } as Partial<EmergencyContactFields>);
    }

    if (detail.section === 'fees') {
      formActions.updateFees({ [detail.field]: detail.value } as Partial<FeesFields>);
    }
  };

  private handleSectionBlur = (event: Event): void => {
    const detail = (event as CustomEvent<{ path: string }>).detail;
    if (!detail?.path) return;
    registrationActions.markTouched(detail.path);
    this.validateForm();
    this.scheduleAutoSave();
  };

  private handleSelectionsChange = (event: Event): void => {
    formActions.updateRunSelections((event as CustomEvent<RunSelectionDraft>).detail);
    formActions.clearValidationErrors();
    this.scheduleAutoSave();
  };

  private handleTermsChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ accepted: boolean }>).detail;
    if (!detail) return;
    formActions.updateTerms({ accepted: detail.accepted });
    registrationActions.markTouched('terms.accepted');
    this.validateForm({ includeTerms: true });
  };

  private handlePremiumChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ field: string; value: string | boolean }>).detail;
    if (!detail) return;
    if (detail.field === 'acknowledged') {
      formActions.updatePremium({ acknowledged: detail.value as boolean });
    } else if (detail.field === 'signature') {
      formActions.updatePremium({ signature: detail.value as string });
    }
  };

  private handleErrorSelected = (event: Event): void => {
    const detail = (event as CustomEvent<string>).detail;
    if (!detail) return;
    this.scrollToControl(detail);
  };

  private handleReviewErrors = (): void => {
    registrationActions.setValidationSummaryVisible(true);
    const errors = this.validateForm({ includeAll: true, includeSelections: true, includeTerms: true });
    if (errors[0]) {
      this.scrollToControl(errors[0].field);
    }
  };

  private handleBack = (): void => {
    window.history.back();
  };

  private scheduleAutoSave(): void {
    if (this.autoSaveTimer) {
      window.clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = window.setTimeout(() => {
      this.autoSaveTimer = null;
      if (formState.get().isDirty) void this.enqueuePersistence(() => this.saveDraft());
    }, 500);
  }

  private enqueuePersistence(operation: () => Promise<void>): Promise<void> {
    const nextOperation = this.persistenceQueue
      .catch(() => undefined)
      .then(operation);

    this.persistenceQueue = nextOperation.then(
      () => undefined,
      () => undefined
    );

    return nextOperation;
  }

  private async saveDraft(): Promise<void> {
    if (this.autoSaveTimer !== null) {
      window.clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    const state = formState.get();
    if (!state.entryId || this.entryStatus !== 'Draft') {
      return;
    }

    registrationActions.clearSubmitError();
    formActions.setSaving(true);
    formActions.setPricingQuote(null);
    const fingerprint = this.draftFingerprint();
    try {
      const fields = this.buildEntryUpdateRequest(state);
      const fieldsKey = JSON.stringify(fields);
      let etag = state.entryEtag;
      if (fieldsKey !== this.savedFields) {
        const response = await entriesApi.update(
          this.trialEventId, state.entryId, fields, state.entryEtag ?? undefined
        );
        if (!this.isCurrentEntry(state.entryId)) return;
        if (response.error || !response.data || !response.etag) {
          this.persistenceError(response.error);
          return;
        }
        this.savedFields = fieldsKey;
        this.savedJuniorDob = response.data.contact.junior?.dob ?? '';
        etag = response.etag;
        formActions.setEntryEtag(etag);
      }
      if (!etag) { this.persistenceError(); return; }
      if (fingerprint !== this.draftFingerprint()) return;
      const selectionResponse = await entriesApi.updateSelections(
        this.trialEventId, state.entryId,
        state.runDraft ?? editRunSelections(state.runSelections ?? { actualHandlers: [], runs: [] }), etag
      );
      if (!this.isCurrentEntry(state.entryId)) return;
      if (selectionResponse.error || !selectionResponse.data || !selectionResponse.etag) {
        this.persistenceError(selectionResponse.error);
        return;
      }
      formActions.setEntryEtag(selectionResponse.etag);
      if (fingerprint !== this.draftFingerprint()) return;
      const saved = selectionResponse.data;
      formActions.loadEntry(state.entryId, {
        runSelections: saved.selections, runDraft: editRunSelections(saved.selections),
        entryRevision: saved.revision, pricingQuote: null,
      });
      await this.refreshValidation();
      toastActions.success('Draft saved');
    } finally {
      if (this.isCurrentEntry(state.entryId)) formActions.setSaving(false);
    }
  }

  private isCurrentEntry(entryId: string): boolean {
    return this.isConnected && formState.get().entryId === entryId && formState.get().trialEventId === this.trialEventId;
  }

  private draftFingerprint(): string {
    const state = formState.get();
    return JSON.stringify([state.dog, state.contact, state.emergencyContact, state.runDraft]);
  }

  private persistenceError(error?: { title: string; errors?: Record<string, string[]> }): void {
    this.applyServerErrors(error?.errors);
    registrationActions.setValidationSummaryVisible(true);
    registrationActions.setSubmitError(error?.title ?? 'The server did not return a saved entry and ETag. Your edits are retained.');
    formActions.setPricingQuote(null);
    formActions.markValidationStale();
  }

  private async refreshValidation(): Promise<void> {
    const state = formState.get();
    if (!state.entryId) return;
    const fingerprint = this.draftFingerprint();
    const etag = state.entryEtag;
    const requestId = ++this.quoteRequestId;
    formActions.setPricingQuote(null);
    formActions.markValidationStale();
    const response = await entriesApi.validate(this.trialEventId, state.entryId);
    if (requestId !== this.quoteRequestId || !this.isCurrentEntry(state.entryId) || etag !== formState.get().entryEtag ||
        fingerprint !== this.draftFingerprint()) return;
    if (response.error || !response.data) {
      this.persistenceError(response.error);
      return;
    }
    formActions.clearValidationErrors();
    this.applyServerErrors(response.data.errors);
    const quote = response.data.calculatedFees;
    if (quote && quote.entryRevision === formState.get().entryRevision &&
        quote.configurationVersion === registrationState.trialConfig.get()?.configurationVersion) {
      formActions.setPricingQuote(quote);
    } else {
      formActions.setPricingQuote(null);
      if (quote) {
        registrationActions.setSubmitError('Trial configuration changed. Reload to review the current offers and prices. Your edits are retained.');
      }
    }
    if (response.data.isValid && formState.get().pricingQuote) formActions.markValidationFresh();
    else formActions.markValidationStale();
  }

  private async submitEntry(): Promise<void> {
    const entryId = formState.get().entryId;
    if (!entryId || formState.get().isSubmitting || this.entryStatus !== 'Draft') {
      return;
    }
    formActions.setSubmitting(true);
    registrationActions.clearSubmitError();
    try {
      if (this.autoSaveTimer !== null) {
        window.clearTimeout(this.autoSaveTimer);
        this.autoSaveTimer = null;
      }
      await this.persistenceQueue;
      if (!this.isCurrentEntry(entryId)) return;
      if (formState.get().isDirty) await this.enqueuePersistence(() => this.saveDraft());
      if (!this.isCurrentEntry(entryId) || formState.get().isDirty) return;
      await this.refreshValidation();
      if (!this.isCurrentEntry(entryId)) return;
      const state = formState.get();
      const errors = this.validateForm({ includeAll: true, includeSelections: true, includeTerms: true });
      if (errors.length > 0 || state.isValidationStale || !state.pricingQuote) {
        registrationActions.setValidationSummaryVisible(true);
        this.scrollToControl(errors[0]?.field ?? 'selections');
        return;
      }
      const response = await entriesApi.submit(this.trialEventId, entryId, {
        acceptTerms: true,
        termsVersion: state.terms.version || registrationState.terms.get()?.version || '',
      });
      if (!this.isCurrentEntry(entryId)) return;
      if (response.error) {
        formActions.setPricingQuote(null);
        formActions.markValidationStale();
        this.applyServerErrors(response.error.errors);
        const supportId = response.supportId || response.error.traceId || '';
        registrationActions.setSubmitError(
          response.error.title || 'Unable to submit entry. Please try again.',
          supportId
        );
        return;
      }
      if (!response.data?.entryId) {
        this.persistenceError();
        return;
      }
      navigate(`/trial-events/${this.trialEventId}/confirmation/${response.data.entryId}`);
    } finally {
      if (this.isCurrentEntry(entryId)) formActions.setSubmitting(false);
    }
  }

  private scrollToControl(path: string): void {
    const element = this.$(`[data-control-path="${path}"]`) as HTMLElement | null ??
      this.$('dt-actual-runs-editor');
    if (!element) return;
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof element.focus === 'function') {
      element.focus({ preventScroll: true });
    }
  }

  private renderLoading(): string {
    return `
      <section class="px-4 py-8" data-testid="loading-state">
        <div class="mx-auto flex min-h-[420px] w-full max-w-4xl items-center justify-center overflow-hidden rounded-[32px] border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="pointer-events-none absolute h-40 w-40 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/25"></div>
          <div class="relative flex flex-col items-center gap-4 px-6 text-center">
            <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
            <dt-spinner size="large"></dt-spinner>
            <div class="space-y-2">
              <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Preparing your registration form</h2>
              <p class="text-sm font-medium text-neutral-600 dark:text-neutral-300">Loading trial details, form metadata, and your draft entry…</p>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderError(message: string): string {
    return `
      <section class="px-4 py-8" data-testid="error-state">
        <div class="mx-auto flex min-h-[420px] w-full max-w-4xl items-center justify-center overflow-hidden rounded-[32px] border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="pointer-events-none absolute h-44 w-44 rounded-full bg-error-50 blur-3xl dark:bg-error-500/10"></div>
          <div class="relative max-w-md px-6 text-center">
            <div class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error-50 text-error-500 ring-8 ring-white dark:bg-error-500/10 dark:ring-surface-dark">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
                <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
              </svg>
            </div>
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-error-700 dark:text-error-300">Registration unavailable</p>
            <h2 class="mt-3 text-2xl font-semibold text-neutral-900 dark:text-white">Unable to load registration</h2>
            <p class="mt-3 text-sm leading-7 text-neutral-600 dark:text-neutral-300">${message}</p>
            <dt-button variant="outline" class="mt-6" data-testid="retry-btn">Retry</dt-button>
          </div>
        </div>
      </section>
    `;
  }

  private renderContent(trial: TrialEventSummaryDto): string {
    const state = formState.get();
    const errors = state.validationErrors;
    const selectionError = errors.find((error) => error.field === 'selections')?.message ?? '';
    const submitError = registrationState.submitErrorMessage.get();
    const submitSupportId = registrationState.submitSupportId.get();
    const validationStale = state.isValidationStale;
    const organizationBadge = trial.formTemplate.organizationCode || trial.hostClubName;
    const formLabel = `${trial.formTemplate.sportCode} ${trial.formTemplate.formCode}`;
    const location = trial.addressCity && trial.addressState
      ? `${trial.addressCity}, ${trial.addressState}`
      : trial.addressCity || trial.addressState || '';

    return `
      <section class="px-4 py-6" data-testid="registration-loaded">
        <div class="mx-auto flex w-full max-w-4xl flex-col gap-6" data-testid="registration-layout">
          <header
            class="sticky top-16 z-20 overflow-hidden rounded-[28px] border border-neutral-200 bg-[linear-gradient(135deg,#fff8ef_0%,#ffffff_52%,#f7f1e3_100%)] shadow-[0_18px_48px_rgba(48,52,37,0.12)] backdrop-blur-sm dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(26,29,20,0.96)_0%,rgba(30,26,24,0.94)_52%,rgba(63,11,28,0.22)_100%)]"
            data-testid="registration-sticky-header"
          >
            <div class="pointer-events-none absolute -left-12 top-0 h-28 w-28 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/20"></div>
            <div class="pointer-events-none absolute -right-8 bottom-0 h-32 w-32 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
            <div class="relative flex flex-col gap-4 px-4 py-5 sm:px-6">
              <div class="flex flex-wrap items-center gap-2">
                <span class="inline-flex items-center rounded-full bg-secondary-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-700 dark:bg-secondary-500/15 dark:text-secondary-200">Handler registration</span>
                <span
                  class="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-primary-700"
                  data-testid="registration-organization-badge"
                >${organizationBadge}</span>
                <span class="text-xs font-medium uppercase tracking-[0.2em] text-neutral-500">${formLabel}</span>
              </div>
              <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h1 class="text-2xl font-bold text-neutral-900">${trial.name}</h1>
                  <div class="mt-1 text-sm font-medium text-neutral-600 dark:text-neutral-300">${trial.hostClubName}</div>
                  <p class="mt-3 max-w-2xl text-sm leading-6 text-neutral-600 dark:text-neutral-300">Complete your dog, contact, and class-selection details below. The form stays in your draft until you explicitly submit it.</p>
                </div>
                <div class="grid gap-1 text-sm text-neutral-600 sm:text-right">
                  <div><span class="font-semibold text-neutral-700">Dates:</span> ${this.formatDateRange(trial)}</div>
                  ${location ? `<div><span class="font-semibold text-neutral-700">Location:</span> ${location}</div>` : ''}
                </div>
              </div>
            </div>
          </header>
          <dt-card variant="outlined" data-testid="registration-card">
            <div class="flex flex-col gap-6">
              <dt-validation-summary
                data-testid="validation-summary"
              ></dt-validation-summary>

              <form class="flex flex-col gap-6" novalidate>
                <fieldset class="contents" ${state.isSaving || state.isSubmitting || this.entryStatus !== 'Draft' ? 'disabled' : ''}>
                <dt-dog-section data-control-path="dog"></dt-dog-section>
                <dt-contact-section data-control-path="contact"></dt-contact-section>

                <section class="flex flex-col gap-3" data-control-path="selections">
                  <dt-actual-runs-editor tabindex="-1"></dt-actual-runs-editor>
                  ${selectionError ? `<p class="text-xs text-error-600">${selectionError}</p>` : ''}
                </section>

                <div class="grid gap-6 lg:grid-cols-2">
                  <dt-emergency-section data-control-path="emergencyContact"></dt-emergency-section>
                  <dt-fees-section data-control-path="fees"></dt-fees-section>
                </div>

                <dt-premium-section data-control-path="premium"></dt-premium-section>

                <dt-terms-section data-control-path="terms.accepted"></dt-terms-section>
                </fieldset>

                ${validationStale ? `
                  <div class="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700 shadow-xs" data-testid="validation-stale-message">
                    Re-validate required — save your draft to re-validate.
                  </div>
                ` : ''}

                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="registration-actions">
                  <dt-button variant="ghost" data-testid="back-btn">Back</dt-button>
                  <div class="flex flex-wrap items-center justify-end gap-3">
                    <dt-button variant="outline" data-testid="save-draft">Save Draft</dt-button>
                    <dt-button variant="ghost" data-testid="review-errors">Review for errors</dt-button>
                    <dt-button
                      variant="primary"
                      data-testid="submit-entry"
                    >Submit Entry</dt-button>
                  </div>
                </div>

                ${submitError ? `
                  <div class="rounded-2xl border border-error-200 bg-error-50 p-4 text-sm text-error-700 shadow-xs">
                    <div class="font-semibold">Submission failed</div>
                    <div class="mt-1">${submitError}</div>
                    ${submitSupportId ? `
                      <div class="mt-2 flex flex-wrap items-center gap-2 text-xs text-error-600">
                        <span>Support ID:</span>
                        <span class="font-mono font-semibold" data-testid="submit-support-id">${submitSupportId}</span>
                      </div>
                    ` : ''}
                  </div>
                ` : ''}
              </form>
            </div>
          </dt-card>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    const restoreFocus = this.retainControlFocus();
    const pageState = registrationComputed.pageState.get();
    let content = '';

    if (pageState === 'loading') {
      content = this.renderLoading();
    } else if (pageState === 'error') {
      content = this.renderError(registrationState.error.get() ?? 'Unable to load.');
    } else {
      const trial = registrationState.trial.get();
      if (trial) {
        content = this.renderContent(trial);
      }
    }

    this.render(content);
    this.attachEventListeners();
    this.assignSectionData();
    restoreFocus();
  }

  private attachEventListeners(): void {
    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => void this.loadRegistration());
    }

    const backBtn = this.$('[data-testid="back-btn"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBack);
    }

    const reviewBtn = this.$('[data-testid="review-errors"]');
    if (reviewBtn) {
      reviewBtn.addEventListener('click', () => this.handleReviewErrors());
    }

    const saveDraftBtn = this.$('[data-testid="save-draft"]');
    if (saveDraftBtn) {
      saveDraftBtn.addEventListener('click', () => void this.enqueuePersistence(() => this.saveDraft()));
    }

    const submitBtn = this.$('[data-testid="submit-entry"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => void this.submitEntry());
    }

    const summary = this.$('dt-validation-summary');
    if (summary) {
      summary.addEventListener('error-selected', this.handleErrorSelected);
    }

    const dogSection = this.$('dt-dog-section');
    if (dogSection) {
      dogSection.addEventListener('section-change', this.handleSectionChange);
      dogSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const contactSection = this.$('dt-contact-section');
    if (contactSection) {
      contactSection.addEventListener('section-change', this.handleSectionChange);
      contactSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const emergencySection = this.$('dt-emergency-section');
    if (emergencySection) {
      emergencySection.addEventListener('section-change', this.handleSectionChange);
      emergencySection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const feesSection = this.$('dt-fees-section');
    if (feesSection) {
      feesSection.addEventListener('section-change', this.handleSectionChange);
      feesSection.addEventListener('section-blur', this.handleSectionBlur);
    }

    const premiumSection = this.$('dt-premium-section');
    if (premiumSection) {
      premiumSection.addEventListener('section-change', this.handlePremiumChange);
    }

    const termsSection = this.$('dt-terms-section');
    if (termsSection) {
      termsSection.addEventListener('terms-change', this.handleTermsChange);
    }

    const grid = this.$('dt-actual-runs-editor');
    if (grid) {
      grid.addEventListener('runs-change', this.handleSelectionsChange);
    }
  }

  private assignSectionData(): void {
    const state = formState.get();
    const errors = state.validationErrors;

    const summary = this.$('dt-validation-summary') as any;
    if (summary) {
      summary.errors = errors;
      summary.visible = registrationState.validationSummaryVisible.get();
    }

    const dogSection = this.$('dt-dog-section') as any;
    if (dogSection) {
      dogSection.dog = state.dog;
      dogSection.errors = errors;
    }

    const contactSection = this.$('dt-contact-section') as any;
    if (contactSection) {
      contactSection.contact = state.contact;
      contactSection.errors = errors;
    }

    const emergencySection = this.$('dt-emergency-section') as any;
    if (emergencySection) {
      emergencySection.emergency = state.emergencyContact;
      emergencySection.errors = errors;
    }

    const feesSection = this.$('dt-fees-section') as FeesSection | null;
    if (feesSection) {
      feesSection.quote = state.pricingQuote;
      feesSection.config = registrationState.trialConfig.get();
    }

    const premiumSection = this.$('dt-premium-section') as any;
    if (premiumSection) {
      const trial = registrationState.trial.get();
      premiumSection.premiumPdfUrl = trial?.premiumPdfUrl ?? null;
      premiumSection.acknowledged = state.premium.acknowledged;
      premiumSection.signature = state.premium.signature;
    }

    const termsSection = this.$('dt-terms-section') as any;
    if (termsSection) {
      termsSection.terms = registrationState.terms.get();
      termsSection.accepted = state.terms.accepted;
      termsSection.error = errors.find((error) => error.field === 'terms.accepted')?.message ?? '';
    }

    const grid = this.$('dt-actual-runs-editor') as ActualRunsEditor | null;
    if (grid) {
      grid.data = {
        config: registrationState.trialConfig.get(),
        selections: state.runDraft ?? editRunSelections(state.runSelections ?? { actualHandlers: [], runs: [] }),
        quote: state.pricingQuote, errors,
        disabled: state.isSaving || state.isSubmitting || this.entryStatus !== 'Draft',
      };
    }

    const submitBtn = this.$('[data-testid="submit-entry"]') as any;
    if (submitBtn) {
      const isSubmitting = state.isSubmitting;
      submitBtn.disabled = !registrationComputed.canSubmit.get() || this.entryStatus !== 'Draft';
      submitBtn.loading = isSubmitting;
    }

    const saveDraftBtn = this.$('[data-testid="save-draft"]') as any;
    if (saveDraftBtn) {
      saveDraftBtn.loading = formState.get().isSaving;
      saveDraftBtn.disabled = state.isSaving || state.isSubmitting || this.entryStatus !== 'Draft';
    }
  }
}

customElements.define('dt-registration-page', RegistrationPage);
